<!--
  A document's actions in LIST view: real buttons, at the right of the row.

  TASK-042 §3b. Not a menu, and not on hover.

  * A control that appears on hover does not exist for a keyboard or a touch
    user, and revealing three buttons on hover makes every row change width
    under the pointer. They are visible at all times.
  * On a narrow viewport the labels collapse to glyphs with an `aria-label`
    that still names the document. That is the one place this set is allowed to
    become less legible, and it never becomes a menu.

  The SET comes from `docActions()`, which `DocRowMenu` also renders, so list
  view and icon view cannot drift into offering different things. Nothing here
  is inferred: `doc.role` comes from the server on every row.

  It takes a DOCUMENT rather than an index, so that if multi-select ever lands
  (§6e) a selection is a set of ids passed to these same call sites.
-->
<script lang="ts">
  import type { DocActionKind, DocRow } from './doclist-types'
  import { docActions } from './doclist-types'

  let {
    doc,
    archived = false,
    dense = false,
    onshare,
    onrename,
    onmove,
    ondelete,
    onleave,
    onrestore,
  }: {
    doc: DocRow
    archived?: boolean
    /**
     * The dense list row (TASK-042, list density). The labels collapse to the
     * same glyphs the narrow viewport already uses, because three words per
     * row is ~200px of a row that is 36px tall and the name column is what
     * pays for it. Still three buttons, still one click each, still named for
     * a screen reader by `aria-label` and on hover by `title` -- the one thing
     * it must never become is a menu.
     */
    dense?: boolean
    onshare: (doc: DocRow) => void
    onrename: (doc: DocRow) => void
    onmove: (doc: DocRow) => void
    ondelete: (doc: DocRow) => void
    onleave: (doc: DocRow) => void
    onrestore: (doc: DocRow) => void
  } = $props()

  const actions = $derived(docActions(doc, { archived }))

  const LABEL: Record<DocActionKind, string> = {
    share: 'Share',
    rename: 'Rename',
    move: 'Move to…',
    delete: 'Delete',
    leave: 'Remove from my list',
    restore: 'Restore',
  }

  const GLYPH: Record<DocActionKind, string> = {
    share: '↗',
    rename: '✎',
    move: '↪',
    delete: '✕',
    leave: '⊘',
    restore: '↩',
  }

  function run(kind: DocActionKind) {
    if (kind === 'share') onshare(doc)
    else if (kind === 'rename') onrename(doc)
    else if (kind === 'move') onmove(doc)
    else if (kind === 'delete') ondelete(doc)
    else if (kind === 'leave') onleave(doc)
    else onrestore(doc)
  }
</script>

<div class="row-actions" class:dense>
  {#each actions as kind (kind)}
    <button
      type="button"
      class="row-action"
      class:row-action-danger={kind === 'delete' || kind === 'leave'}
      aria-label={`${LABEL[kind]} ${doc.name}`}
      title={LABEL[kind]}
      onclick={() => run(kind)}
    >
      <span class="row-action-label">{LABEL[kind]}</span>
      <span class="row-action-glyph" aria-hidden="true">{GLYPH[kind]}</span>
    </button>
  {/each}
</div>

<style>
  .row-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.15rem;
  }

  .row-action {
    border: 0;
    background: transparent;
    color: var(--color-ink-soft, #6e665c);
    font: inherit;
    font-size: var(--text-meta, 0.8125rem);
    padding: 0.3rem 0.45rem;
    border-radius: 0.375rem;
    cursor: pointer;
    white-space: nowrap;
  }

  .row-action:hover {
    background: var(--color-plinth, #f1ece4);
    color: var(--color-ink, #211d18);
  }

  .row-action-danger {
    color: var(--color-red-deep, #c01030);
  }

  .row-action-glyph {
    display: none;
  }

  /* The dense row: the same trade the narrow viewport makes below, taken on
     purpose rather than by width. Every button keeps its `aria-label` and its
     `title`, so the word is still there for a screen reader and for a pointer
     that rests. */
  .row-actions.dense .row-action-label {
    display: none;
  }

  .row-actions.dense .row-action-glyph {
    display: inline;
  }

  .row-actions.dense .row-action {
    padding: 0.2rem 0.4rem;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  /* The narrow end of TASK-042 §4's two breakpoints. The labels go, the
     buttons stay: less legible is the trade, hidden behind a menu is not. */
  @media (max-width: 640px) {
    .row-action-label {
      display: none;
    }

    .row-action-glyph {
      display: inline;
    }
  }
</style>
